import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, CheckCircle, AlertTriangle, Scale, Ban, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
} from '@/components/ui/OptimizedAnimations';

const sections = [
  {
    icon: CheckCircle,
    title: 'Acceptance of Terms',
    content: `By accessing or using Tattva Techworkz services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.

These terms apply to all users of our platform, including institutions, administrators, teachers, students, and parents.`,
  },
  {
    icon: FileText,
    title: 'Service Description',
    content: `Tattva Techworkz provides educational technology solutions including:

• School ERP - Campus management system for K-12 institutions
• College ERP - Higher education management platform
• Lynkt - Teacher professional development platform
• LMS - Learning management system

We reserve the right to modify, suspend, or discontinue any part of our services with reasonable notice.`,
  },
  {
    icon: Scale,
    title: 'User Responsibilities',
    content: `As a user of our services, you agree to:

• Provide accurate and complete information during registration
• Maintain the confidentiality of your account credentials
• Use the services only for lawful educational purposes
• Not attempt to gain unauthorized access to any part of the system
• Not upload malicious code or interfere with service operation
• Comply with all applicable laws and regulations
• Respect the intellectual property rights of others`,
  },
  {
    icon: AlertTriangle,
    title: 'Data and Content',
    content: `You retain ownership of all data you upload to our platform. By using our services, you grant us a limited license to:

• Store and process your data to provide the services
• Create backups for disaster recovery
• Generate anonymized analytics to improve our products

We do not sell your data to third parties. Student data is handled in accordance with applicable education privacy laws.`,
  },
  {
    icon: Ban,
    title: 'Prohibited Activities',
    content: `The following activities are strictly prohibited:

• Sharing account credentials with unauthorized parties
• Using the platform for any illegal or unauthorized purpose
• Attempting to reverse engineer or copy our software
• Harassing, threatening, or abusing other users
• Uploading content that violates intellectual property rights
• Interfering with the security or integrity of the platform
• Using automated systems to access the services without permission

Violation of these terms may result in immediate account termination.`,
  },
  {
    icon: FileText,
    title: 'Payment Terms',
    content: `For paid services:

• Fees are billed according to the selected subscription plan
• Payment is due within 30 days of invoice date
• Late payments may incur interest at 1.5% per month
• Annual subscriptions are non-refundable after 30 days
• We reserve the right to modify pricing with 60 days notice
• Taxes are additional where applicable`,
  },
  {
    icon: Scale,
    title: 'Limitation of Liability',
    content: `To the maximum extent permitted by law:

• Our services are provided "as is" without warranties
• We are not liable for indirect, incidental, or consequential damages
• Our total liability shall not exceed the fees paid in the last 12 months
• We are not responsible for third-party content or services

This limitation applies to all claims, whether based on warranty, contract, tort, or any other legal theory.`,
  },
  {
    icon: FileText,
    title: 'Termination',
    content: `Either party may terminate the agreement:

• With 30 days written notice for convenience
• Immediately for material breach not cured within 15 days
• Immediately for non-payment exceeding 60 days

Upon termination:
• Access to services will be suspended
• You may request data export within 30 days
• We will delete your data within 90 days unless legally required to retain`,
  },
];

const TermsPage = () => {
  return (
    <div className="relative bg-void min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-mesh-gradient opacity-50" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal delay={0.1}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-teal text-sm mb-8 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </ScrollReveal>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-orange/20 flex items-center justify-center">
              <FileText className="w-7 h-7 text-orange" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-display font-bold text-foreground">
                Terms of Service
              </h1>
              <p className="text-muted-foreground text-sm">
                Last updated: July 2026
              </p>
            </div>
          </div>

          <ScrollReveal delay={0.3}>
            <p className="text-muted-foreground text-lg mb-8">
              Please read these Terms of Service carefully before using our platform.
              These terms govern your access to and use of Tattva Techworkz services.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 bg-void-light/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerContainer staggerDelay={0.08} className="space-y-6">
            {sections.map((section, index) => (
              <StaggerItem key={section.title}>
                <motion.div
                  whileHover={{ y: -2 }}
                  className="glass-card p-8"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-orange/20 flex items-center justify-center flex-shrink-0">
                      <section.icon className="w-5 h-5 text-orange" />
                    </div>
                    <h2 className="text-xl font-display font-semibold text-foreground">
                      {index + 1}. {section.title}
                    </h2>
                  </div>
                  <div className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line pl-14">
                    {section.content}
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-void">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h3 className="text-display text-2xl text-foreground mb-4">
              Questions About Terms?
            </h3>
            <p className="text-muted-foreground mb-6">
              If you have any questions about these Terms of Service, please contact our legal team.
            </p>
            <div className="inline-flex items-center gap-3 glass-card px-6 py-4">
              <Mail className="w-5 h-5 text-orange" />
              <a
                href="mailto:legal@tattvatechworkz.com"
                className="text-orange hover:text-orange-light transition-colors"
              >
                legal@tattvatechworkz.com
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
